import React, { useEffect, useState } from 'react';
import Img from 'gatsby-image';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { InView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { media } from '../../../utils/media-queries';
import { useWindowSize } from '../../../hooks/useWindowSize';
import HorizontalScroll from '../../organisms/horizontalScroll';
import CtaLink from '../../atoms/ctaLink';

const List = ({ title, items }) => {
  const size = useWindowSize();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (size.width < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [size]);

  return (
    <StyledList className="spacing-removed">
      <HorizontalScroll>
        <p className="bold-small">{title}</p>
        <div
          className="wrapper"
          style={{
            width: isMobile
              ? `${items.length * 65}vw`
              : `${items.length * 28}vw`,
          }}
        >
          {items.map((item, i) => (
            <InView key={i} threshold={0.7}>
              {({ inView, ref, entry }) => (
                <motion.div
                  ref={ref}
                  className="person"
                  initial={{ opacity: 0.3 }}
                  animate={{ opacity: inView ? 1 : 0.3 }}
                  transition={{ duration: 1 }}
                >
                  <div className="title">
                    <p className="name">{item.name}</p>
                    <p className="overline">{item.jobTitle}</p>
                  </div>
                  <figure>
                    <Img
                      fluid={item.image?.image?.asset.fluid}
                      alt={item.image?.alt}
                    />
                  </figure>
                  <p className="desc">{item.description}</p>
                  <div className="links">
                    {item.__typename === 'SanityPerson' ? (
                      <>
                        <CtaLink
                          link={item.linkedinUrl}
                          ctaText="LinkedIn"
                          internalLink={false}
                        />
                        <CtaLink
                          link={`mailto:${item.mailAddress}`}
                          ctaText="Mail"
                          internalLink={false}
                        />
                      </>
                    ) : (
                      <CtaLink
                        link={item.url}
                        ctaText="Visit"
                        internalLink={false}
                      />
                    )}
                  </div>
                </motion.div>
              )}
            </InView>
          ))}
        </div>
      </HorizontalScroll>
    </StyledList>
  );
};

const StyledList = styled.section`
  color: var(--blue);
  padding-bottom: calc(var(--v-spacing-L) * 3);

  .bold-small {
    padding-left: var(--spacing-L);
    padding-bottom: var(--v-spacing-L);
  }

  .wrapper {
    padding-left: var(--spacing-L);
    width: 100%;
    display: flex;

    .person {
      padding-right: var(--spacing-XXS);
      width: 30vh;

      .title {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
        min-height: calc(var(--font-size-2) * 2.8);
      }

      .name {
        font-family: var(--font-2);
        padding-bottom: calc(var(--spacing-XXS) / 2);
      }

      .gatsby-image-wrapper {
        width: 100%;
      }

      .desc,
      .link {
        font-size: var(--font-size-0);
        line-height: 1.3;
      }

      .link {
        padding-bottom: var(--spacing-XXXS);
      }

      .desc {
        padding: var(--spacing-XS) 0;
        min-height: calc(var(--spacing-XL) * 1.5);
        min-height: 42%;
      }

      .links {
        display: flex;
        flex-direction: column;
      }
    }
  }

  .link {
    a {
      margin-left: calc(var(--spacing-XXXS) / 2);
    }
  }

  @media ${media.L} {
    .bold-small {
      padding-left: calc(var(--spacing-XXL) * 2.5);
    }

    .wrapper {
      padding-left: calc(var(--spacing-XXL) * 2.5);

      .person {
        width: 37vh;

        .desc {
          max-width: 85%;
          min-height: 23rem;
        }

        .link {
          padding-bottom: 0;
        }
      }
    }
  }

  @media ${media.XL} {
    .wrapper {
      .person {
        width: 50vh;

        .desc {
          max-width: 85%;
          min-height: 17rem;
        }
      }
    }
  }

  @media ${media.XS} {
    .wrapper {
      .person {
        .desc {
          max-width: 100%;
          min-height: 17rem;
          font-size: 12px;
        }

        .gatsby-image-wrapper {
          width: 70vw;
          max-height: 30vw;
        }
      }
    }
  }
`;

List.propTypes = {
  items: PropTypes.array,
  title: PropTypes.string,
};

export default List;
